import React, { useRef, useState, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import jsPDF from 'jspdf';
import './WaiverModal.css';

export default function WaiverModal({ open, onClose }) {
  const overlayRef = useRef(null);
  const sigPad = useRef({});
  // Assume same base URL as request modal, but it could be passed as a prop
  const ENDPOINT = 'https://goldlawsweetjustice.com/wp-json/sj/v1/waiver';
  const [status, setStatus] = useState({ state: 'idle', msg: '' });

  const [formData, setFormData] = useState({
    hostName: '',
    repName: '',
    repTitle: '',
    eventDate: '',
    startTime: '',
    endTime: '',
    locationName: '',
    address: '',
    attendance: '',
    phone: '',
    email: '',
    initials: '',
    ageCert: false,
    electronicConsent: false
  });

  // Close handlers
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    const onClick = (e) => {
      if (overlayRef.current && e.target === overlayRef.current) {
        onClose?.();
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('click', onClick, true);
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('click', onClick, true);
    };
  }, [open, onClose]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const clearSignature = () => {
    sigPad.current.clear();
  };

  const generatePDF = (shouldSave = true, options = {}) => {
    const { blank = false } = options;
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);
    let y = 20;

    // Helper for text wrapping
    const addText = (text, fontSize = 10, font = 'times', style = 'normal', align = 'left') => {
      doc.setFont(font, style);
      doc.setFontSize(fontSize);
      const lines = doc.splitTextToSize(text, contentWidth);
      doc.text(lines, align === 'center' ? pageWidth / 2 : margin, y, { align: align });
      y += (lines.length * fontSize * 0.4) + 2;
    };

    const addSpace = (h) => { y += h; };

    addSpace(10);

    // Title
    addText("GOLDLAW PERSONAL INJURY LAWYERS", 14, 'helvetica', 'bold', 'center');
    addSpace(5);
    addText("SWEET JUSTICE ICE CREAM TRUCK EVENT AGREEMENT", 12, 'helvetica', 'bold', 'center');
    addText("AND WAIVER & RELEASE OF LIABILITY", 12, 'helvetica', 'bold', 'center');
    addSpace(15);

    // Body Text
    addText("For Groups & Organizations in Florida", 11, 'times', 'bold');
    addSpace(5);
    
    const intro = "This Waiver and Release of Liability (the \"Agreement\") is entered into between GOLDLAW, P.A. (and GOLDLAW SWEET JUSTICE, LLC, who shall be known as \"THE GOLDLAW ENTITIES\"), and the undersigned Organization (the \"Host Organization\") in connection with the participation of GOLDLAW's \"Sweet Justice\" ice cream truck at an event hosted by the Host Organization.";
    addText(intro);
    addSpace(5);
    addText("By signing below, the Host Organization acknowledges and agrees to the following:");
    addSpace(10);

    addText("1. ASSUMPTION OF RISK", 11, 'times', 'bold');
    addText("The Host Organization understands and acknowledges that:");
    const riskPoints = [
      "• The GOLDLAW Sweet Justice ice cream truck provides ice cream and frozen treats while supplies last.",
      "• Consumption of food and beverages carries inherent risks, including but not limited to allergic reactions, choking hazards, foodborne illnesses, and adverse medical reactions.",
      "• GOLDLAW Sweet Justice makes reasonable efforts to ensure food safety but cannot guarantee that ice cream or treats are free from allergens, contaminants, or other risks.",
      "• The Host Organization is responsible for notifying attendees of any potential risks associated with consuming ice cream or frozen treats."
    ];
    riskPoints.forEach(p => addText(p));
    addSpace(10);

    addText("2. RELEASE OF LIABILITY", 11, 'times', 'bold');
    addText("To the fullest extent permitted by law, the Host Organization, on behalf of itself, its officers, employees, volunteers, participants, and guests, releases, waives, and discharges THE GOLDLAW ENTITIES from any and all claims arising from related to:");
    const releasePoints = [
      "• Injury, illness, or damages resulting from the consumption of food or beverages distributed by the GOLDLAW Sweet Justice ice cream truck.",
      "• Slips, falls, or any other accidents occurring in the vicinity of the truck.",
      `• INITIAL HERE: ${blank ? '__________' : formData.initials} Any incident allegedly caused by the GOLDLAW Sweet Justice ice cream truck.`,
      "• Any NEGLIGENT acts by any of THE GOLDLAW ENTITIES' employees, agents, independent contractors, or anyone acting on behalf of THE GOLDLAW ENTITIES."
    ];
    releasePoints.forEach(p => addText(p));
    addSpace(10);

    if (y > 250) { doc.addPage(); y = 20; }

    addText("3. INDEMNIFICATION", 11, 'times', 'bold');
    addText("The Host Organization agrees to indemnify, defend, and hold harmless the Released Parties from and against any claims, demands, losses, liabilities, or expenses arising from THE GOLDLAW ENTITIES' arising from any claims, including but not limited to, direct negligence by THE GOLDLAW ENTITIES.");
    addSpace(10);

    addText("4. EVENT LOGISTICS", 11, 'times', 'bold');
    addText("The Host Organization agrees to:");
    addText("• Provide a designated parking space suitable for the GOLDLAW Sweet Justice ice cream truck.");
    addText("• Obtain any necessary permits or permissions required for the event.");
    addSpace(10);

    addText("5. INSURANCE", 11, 'times', 'bold');
    addText("The Host Organization agrees to provide THE GOLDLAW ENTITIES with a certificate of insurance at least 48 hours prior to the event, to sweetjustice@goldlaw.com with limits of no less than $250,000 for general liability.");
    addSpace(10);

    addText("6. MISCELLANEOUS", 11, 'times', 'bold');
    const miscPoints = [
      "• This Agreement shall be governed by and construed in accordance with the laws of the State of Florida. Sole venue for any disputes under this agreement is Palm Beach County, Florida.",
      "• If any provision of this Agreement is found to be invalid or unenforceable, the remainder shall remain in full force and effect.",
      "• This Agreement constitutes the entire understanding between the parties regarding the GOLDLAW Sweet Justice ice cream truck’s participation in the event."
    ];
    miscPoints.forEach(p => addText(p));
    addSpace(5);
    addText("By signing below, the Host Organization acknowledges that they have read, understand, and voluntarily agree to the terms of this Agreement.");
    addSpace(15);

    // Host Info
    if (y > 200) { doc.addPage(); y = 20; }
    addText("HOST ORGANIZATION INFORMATION", 12, 'helvetica', 'bold');
    addSpace(5);
    
    const fields = [
      `Organization: ${blank ? '____________________' : formData.hostName}`,
      `Representative: ${blank ? '____________________' : formData.repName}`,
      `Title: ${blank ? '____________________' : formData.repTitle}`,
      `Event Date: ${blank ? '____________________' : formData.eventDate}`,
      `Time: ${blank ? '____________________' : `${formData.startTime} - ${formData.endTime}`}`,
      `Location: ${blank ? '____________________' : formData.locationName}`,
      `Address: ${blank ? '____________________' : formData.address}`,
      `Attendance: ${blank ? '____________________' : formData.attendance}`,
      `Phone: ${blank ? '____________________' : formData.phone}`,
      `Email: ${blank ? '____________________' : formData.email}`
    ];
    
    doc.setFont('courier', 'normal');
    fields.forEach(f => addText(f));
    addSpace(15);

    // Signature
    if (y > 220) { doc.addPage(); y = 20; }
    addText(blank ? "SIGNATURE (HAND-SIGNED)" : "SIGNATURE", 12, 'helvetica', 'bold');
    if (!blank && !sigPad.current.isEmpty()) {
      const sigData = sigPad.current.toDataURL();
      doc.addImage(sigData, 'PNG', margin, y, 60, 30);
      y += 35;
    } else if (blank) {
      addText('Signature: ________________________________');
      addText('Print Name: ______________________________');
      y += 5;
    }
    addText(`Signed by: ${blank ? '____________________' : formData.repName}`);
    addText(`Date: ${blank ? '____________________' : new Date().toLocaleDateString()}`);
    if (!blank) {
      addText(`IP Address: [Captured at submission]`);
      addSpace(15);

      // Electronic Consent
      if (y > 240) { doc.addPage(); y = 20; }
      addText("Electronic Signature Consent*", 11, 'helvetica', 'bold');
      addSpace(5);
      addText("By checking here, you are consenting to the use of your electronic signature in lieu of an original signature on paper. You have the right to request that you sign a paper copy instead. By checking here, you are waiving that right. After consent, you may, upon written request to us, obtain a paper copy of an electronic record. No fee will be charged for such copy and no special hardware or software is required to view it. Your agreement to use an electronic signature with us for any documents will continue until such time as you notify us in writing that you no longer wish to use an electronic signature. There is no penalty for withdrawing your consent. You should always make sure that we have a current email address in order to contact you regarding any changes, if necessary.");
    }
    
    if (shouldSave) {
      doc.save(blank ? "Sweet_Justice_Waiver_Blank_Unsigned.pdf" : "Sweet_Justice_Waiver.pdf");
    }
    // Return base64 string (without the data:application/pdf;base64, prefix) to send to server
    const dataUri = doc.output('datauristring');
    return dataUri.split(',')[1];
  };

  const handleDownloadBlank = () => {
    generatePDF(true, { blank: true });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (sigPad.current.isEmpty()) {
      alert("Please sign the waiver.");
      return;
    }
    
    // Generate PDF in memory first
    const pdfBase64 = generatePDF(false);

    // Prepare data for backend submission
    const signatureData = sigPad.current.toDataURL();
    const payload = {
      ...formData,
      signatureData,
      pdfBase64,
    };

    setStatus({ state: 'submitting', msg: 'Sending...' });

    // Submit to WordPress Backend
    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    .then(res => {
      if (!res.ok) throw new Error('Failed to submit waiver');
      return res.json();
    })
    .then(data => {
      console.log('Waiver saved:', data);
      setStatus({ state: 'success', msg: 'Saved!' });
      // Save PDF for user download
      generatePDF(true);
      onClose();
    })
    .catch(err => {
      console.error(err);
      // Fallback: allow download even if server fails? Or block?
      // For now, alert error but allow PDF download so they have a copy
      alert("There was a problem saving your waiver to the server, but your PDF is generating.");
      generatePDF(true);
      onClose();
    })
    .finally(() => {
      setStatus({ state: 'idle', msg: '' });
    });
  };

  if (!open) return null;

  return (
    <div className="waiver-overlay">
      <div className="waiver-modal">
        <div className="waiver-header">
          <h2 className="waiver-title">Event Waiver & Agreement</h2>
          <button className="waiver-close" onClick={onClose}>&times;</button>
        </div>
        
        <div className="waiver-content">
          <div className="waiver-document">
            <div className="waiver-logo-header">
              <div className="waiver-logo-text">GOLDLAW PERSONAL INJURY LAWYERS</div>
              <div className="waiver-subtitle">SWEET JUSTICE ICE CREAM TRUCK EVENT AGREEMENT</div>
            </div>

            <p><strong>For Groups & Organizations in Florida</strong></p>
            <p>This Waiver and Release of Liability (the "Agreement") is entered into between GOLDLAW, P.A. (and GOLDLAW SWEET JUSTICE, LLC, who shall be known as "THE GOLDLAW ENTITIES"), and the undersigned Organization (the "Host Organization") in connection with the participation of GOLDLAW’s "Sweet Justice" ice cream truck at an event hosted by the Host Organization.</p>
            <p>By signing below, the Host Organization acknowledges and agrees to the following:</p>

            <div className="waiver-section">
              <h3>1. ASSUMPTION OF RISK</h3>
              <p>The Host Organization understands and acknowledges that:</p>
              <ul>
                <li>The GOLDLAW Sweet Justice ice cream truck provides ice cream and frozen treats while supplies last.</li>
                <li>Consumption of food and beverages carries inherent risks, including but not limited to allergic reactions, choking hazards, foodborne illnesses, and adverse medical reactions.</li>
                <li>GOLDLAW Sweet Justice makes reasonable efforts to ensure food safety but cannot guarantee that ice cream or treats are free from allergens, contaminants, or other risks.</li>
                <li>The Host Organization is responsible for notifying attendees of any potential risks associated with consuming ice cream or frozen treats.</li>
              </ul>
            </div>

            <div className="waiver-section">
              <h3>2. RELEASE OF LIABILITY</h3>
              <p>To the fullest extent permitted by law, the Host Organization, on behalf of itself, its officers, employees, volunteers, participants, and guests, releases, waives, and discharges THE GOLDLAW ENTITIES, its owners, shareholders, employees, agents, vendors, affiliates, and representatives (collectively, the "Released Parties") from any and all claims, liabilities, demands, damages, costs, or causes of action arising from or related to:</p>
              <ul>
                <li>Injury, illness, or damages resulting from the consumption of food or beverages distributed by the GOLDLAW Sweet Justice ice cream truck.</li>
                <li>Slips, falls, or any other accidents occurring in the vicinity of the truck.</li>
                <li>Any incidents or risks associated with the GOLDLAW Sweet Justice ice cream truck's presence at the event.</li>
                <li style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  INITIAL HERE: 
                  <input 
                    type="text" 
                    name="initials"
                    value={formData.initials}
                    onChange={handleChange}
                    style={{ width: '60px', padding: '4px', margin: '0 5px' }}
                    required
                    maxLength="3"
                  />
                  Any incident allegedly caused by the GOLDLAW Sweet Justice ice cream truck.
                </li>
                <li>Any NEGLIGENT acts by any of THE GOLDLAW ENTITIES' employees, agents, independent contractors, or anyone acting on behalf of THE GOLDLAW ENTITIES.</li>
              </ul>
            </div>
            
            <div className="waiver-section">
              <h3>3. INDEMNIFICATION</h3>
              <p>The Host Organization agrees to indemnify, defend, and hold harmless the Released Parties from and against any claims, demands, losses, liabilities, or expenses (including attorneys’ fees) arising from THE GOLDLAW ENTITIES’ arising from any claims, including but not limited to, direct negligence by THE GOLDLAW ENTITIES, failure to provide necessary warnings, or any incidents occurring during the event.</p>
            </div>
            
            <div className="waiver-section">
              <h3>4. EVENT LOGISTICS</h3>
              <p>The Host Organization agrees to:</p>
              <ul>
                <li>Provide a designated parking space suitable for the GOLDLAW Sweet Justice ice cream truck.</li>
                <li>Obtain any necessary permits or permissions required for the event.</li>
              </ul>
            </div>

            <div className="waiver-section">
              <h3>5. INSURANCE</h3>
              <p>The Host Organization agrees to provide THE GOLDLAW ENTITIES with a certificate of insurance at least 48 hours prior to the event, to sweetjustice@goldlaw.com with limits of no less than $250,000 for general liability which would provide coverage for any and all activities involved with the use of the GOLDLAW Sweet Justice ice cream truck. There is no third-party beneficiary to this coverage as the obligation to provide such coverage only inures to THE GOLDLAW ENTITIES.</p>
            </div>

            <div className="waiver-section">
              <h3>6. MISCELLANEOUS</h3>
              <ul>
                <li>This Agreement shall be governed by and construed in accordance with the laws of the State of Florida. Sole venue for any disputes under this agreement is Palm Beach County, Florida.</li>
                <li>If any provision of this Agreement is found to be invalid or unenforceable, the remainder shall remain in full force and effect.</li>
                <li>This Agreement constitutes the entire understanding between the parties regarding the GOLDLAW Sweet Justice ice cream truck’s participation in the event.</li>
              </ul>
              <p style={{marginTop: '15px'}}>By signing below, the Host Organization acknowledges that they have read, understand, and voluntarily agree to the terms of this Agreement.</p>
            </div>

            <form id="waiverForm" onSubmit={handleSubmit}>
              <div style={{ marginBottom: '20px', padding: '20px', background: '#f8f9fa', borderRadius: '8px' }}>
                <h3 style={{ fontFamily: 'sans-serif', fontSize: '1rem', fontWeight: '700', textTransform: 'uppercase', marginBottom: '15px' }}>
                  HOST ORGANIZATION INFORMATION: Age Acknowledgment*
                </h3>
                <label className="waiver-checkbox-label" style={{ marginTop: '0' }}>
                  <input type="checkbox" name="ageCert" checked={formData.ageCert} onChange={handleChange} required />
                  I certify that I am 18 years of age or older
                </label>
              </div>

              <div className="waiver-form-grid full-width">
                <h3>HOST ORGANIZATION INFORMATION: Information</h3>
                
                <div className="waiver-field">
                  <label>Legal Name of Organization</label>
                  <input name="hostName" value={formData.hostName} onChange={handleChange} required />
                </div>
                
                <div className="waiver-field">
                  <label>Authorized Representative Name</label>
                  <input name="repName" value={formData.repName} onChange={handleChange} required />
                </div>

                <div className="waiver-field">
                  <label>Title</label>
                  <input name="repTitle" value={formData.repTitle} onChange={handleChange} required />
                </div>

                <div className="waiver-field">
                  <label>Event Date</label>
                  <input type="date" name="eventDate" value={formData.eventDate} onChange={handleChange} required />
                </div>

                <div className="waiver-field">
                  <label>Start Time</label>
                  <input type="time" name="startTime" value={formData.startTime} onChange={handleChange} required />
                </div>

                <div className="waiver-field">
                  <label>End Time</label>
                  <input type="time" name="endTime" value={formData.endTime} onChange={handleChange} required />
                </div>

                <div className="waiver-field full-width">
                  <label>Location Name</label>
                  <input name="locationName" value={formData.locationName} onChange={handleChange} required />
                </div>

                <div className="waiver-field full-width">
                  <label>Exact Address</label>
                  <input name="address" value={formData.address} onChange={handleChange} required />
                </div>

                <div className="waiver-field">
                  <label>Estimated Attendance</label>
                  <input name="attendance" value={formData.attendance} onChange={handleChange} required />
                </div>

                <div className="waiver-field">
                  <label>Phone</label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required />
                </div>

                <div className="waiver-field full-width">
                  <label>Email</label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} required />
                </div>
                
                <div className="waiver-consent-section full-width" style={{ marginTop: '20px' }}>
                  <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '10px' }}>Electronic Signature Consent*</label>
                  <label className="waiver-checkbox-label" style={{ alignItems: 'flex-start', lineHeight: '1.4' }}>
                    <input 
                      type="checkbox" 
                      name="electronicConsent" 
                      checked={formData.electronicConsent} 
                      onChange={handleChange} 
                      required 
                      style={{ marginTop: '4px' }}
                    />
                    <span>
                      By checking here, you are consenting to the use of your electronic signature in lieu of an original
                      signature on paper. You have the right to request that you sign a paper copy instead. By checking
                      here, you are waiving that right. After consent, you may, upon written request to us, obtain a paper
                      copy of an electronic record. No fee will be charged for such copy and no special hardware or
                      software is required to view it. Your agreement to use an electronic signature with us for any
                      documents will continue until such time as you notify us in writing that you no longer wish to use
                      an electronic signature. There is no penalty for withdrawing your consent. You should always make
                      sure that we have a current email address in order to contact you regarding any changes, if
                      necessary.
                    </span>
                  </label>
                </div>
              </div>

              <div className="waiver-signature-section">
                <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>Sign Below:</label>
                <SignatureCanvas 
                  penColor='black'
                  canvasProps={{className: 'signature-pad'}}
                  ref={sigPad}
                />
                <button type="button" onClick={clearSignature} style={{ fontSize: '0.8rem', marginTop: '5px', textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer' }}>Clear Signature</button>
              </div>
            </form>
          </div>
        </div>
        
        <div className="waiver-actions">
          <button type="button" className="btn-waiver btn-waiver-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn-waiver btn-waiver-secondary" onClick={handleDownloadBlank}>
            Download Blank (Unsigned)
          </button>
          <button type="submit" form="waiverForm" className="btn-waiver btn-waiver-primary" disabled={status.state === 'submitting'}>
            {status.state === 'submitting' ? 'Sending...' : 'Sign & Download PDF'}
          </button>
        </div>
      </div>
    </div>
  );
}
